import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Star, Award, MapPin, Calendar, ArrowLeft, MessageSquare, Flag, Send } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import ReportDialog from "@/components/ReportDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function getPhotoUrl(photo) {
  if (!photo) return null;
  if (photo.startsWith("/api/")) return `${BACKEND_URL}${photo}`;
  return photo;
}

export default function UserProfilePage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportOpen, setReportOpen] = useState(false);

  useEffect(() => {
    if (!userId) return;
    api.get(`/users/${userId}`)
      .then(r => setUser(r.data))
      .catch(() => toast.error("Utilisateur introuvable"))
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-4 border-zinc-300 border-t-emerald-700 rounded-full" /></div>;
  if (!user) return <div className="max-w-2xl mx-auto px-4 py-12 text-center text-zinc-500">Utilisateur introuvable</div>;

  const photoUrl = getPhotoUrl(user.profile_photo);
  const initial = user.name?.charAt(0)?.toUpperCase() || "?";
  const joined = (() => { try { return format(new Date(user.created_at), "MMMM yyyy", { locale: fr }); } catch { return ""; } })();

  // rater_role === "host" -> user was rated AS A GUEST
  // rater_role === "guest" -> user was rated AS A HOST
  const asCamper = (user.ratings || []).filter(r => r.rater_role === "host");
  const asHost = (user.ratings || []).filter(r => r.rater_role === "guest");

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Link to="/requests" className="text-sm text-zinc-500 hover:text-zinc-900 flex items-center gap-1 mb-4" data-testid="back-link">
        <ArrowLeft className="h-3.5 w-3.5" /> Retour
      </Link>

      {/* Header card */}
      <Card className="border border-zinc-200 rounded-2xl mb-6" data-testid="user-header-card">
        <CardContent className="p-6 flex flex-col sm:flex-row items-start gap-5">
          <div className="h-24 w-24 rounded-2xl bg-emerald-100 flex items-center justify-center overflow-hidden flex-shrink-0">
            {photoUrl ? <img src={photoUrl} alt={user.name} className="w-full h-full object-cover" data-testid="user-photo" /> : <span className="text-3xl font-bold text-emerald-700">{initial}</span>}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <h1 className="font-heading text-2xl font-bold text-zinc-950" data-testid="user-name">{user.name}</h1>
              {user.is_superstar && (
                <Badge className="bg-gradient-to-r from-amber-400 to-amber-500 text-white border-0 gap-1 shadow-sm" data-testid="user-superstar-badge">
                  <Award className="h-3 w-3" /> Hôte Superstar
                </Badge>
              )}
            </div>
            {user.rating_count > 0 ? (
              <div className="flex items-center gap-1 mb-2" data-testid="user-rating">
                <div className="flex items-center gap-0.5">
                  {[1,2,3,4,5].map(s => <Star key={s} className={`h-4 w-4 ${s <= Math.round(user.avg_rating) ? "fill-amber-400 text-amber-400" : "text-zinc-300"}`} />)}
                </div>
                <span className="font-bold text-zinc-900 text-sm ml-1">{user.avg_rating}/5</span>
                <span className="text-xs text-zinc-500">({user.rating_count} avis)</span>
              </div>
            ) : (
              <Badge className="bg-zinc-100 text-zinc-600 border-0 mb-2">Nouveau membre</Badge>
            )}
            {joined && <p className="text-xs text-zinc-400 flex items-center gap-1 mb-3"><Calendar className="h-3 w-3" /> Membre depuis {joined}</p>}
            {user.bio && <p className="text-sm text-zinc-600 leading-relaxed" data-testid="user-bio">{user.bio}</p>}
            {currentUser && currentUser.id !== userId && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-8 gap-1.5"
                  onClick={() => navigate(`/messages?partner=${userId}&name=${encodeURIComponent(user.name || "")}`)}
                  data-testid="message-user-btn"
                >
                  <Send className="h-3.5 w-3.5" /> Lui écrire
                </Button>
                <button
                  type="button"
                  onClick={() => setReportOpen(true)}
                  className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-red-600 transition-colors"
                  data-testid="report-user-btn"
                >
                  <Flag className="h-3 w-3" /> Signaler cet utilisateur
                </button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Reviews */}
      <Card className="border border-zinc-200 rounded-2xl" data-testid="reviews-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-emerald-600" /> Avis reçus ({user.rating_count || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(!user.ratings || user.ratings.length === 0) ? (
            <div className="text-center py-8 text-sm text-zinc-400" data-testid="no-reviews">
              <Star className="h-8 w-8 mx-auto mb-2 text-zinc-200" />
              Aucun avis pour le moment
            </div>
          ) : (
            <div className="space-y-6">
              {/* As camper (rated by hosts) */}
              {asCamper.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-[0.15em] text-emerald-700 mb-3">En tant que campeur ({asCamper.length})</h3>
                  <div className="space-y-3">
                    {asCamper.map((r, i) => <ReviewItem key={r.id || i} r={r} index={i} prefix="camper" />)}
                  </div>
                </div>
              )}
              {/* As host (rated by guests) */}
              {asHost.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-[0.15em] text-amber-700 mb-3">En tant que propriétaire ({asHost.length})</h3>
                  <div className="space-y-3">
                    {asHost.map((r, i) => <ReviewItem key={r.id || i} r={r} index={i} prefix="host" />)}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <ReportDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        targetType="user"
        targetId={userId}
        targetLabel={user?.name}
      />
    </div>
  );
}

function ReviewItem({ r, index, prefix }) {
  const photoUrl = getPhotoUrl(r.rater_photo);
  const initial = r.rater_name?.charAt(0)?.toUpperCase() || "?";
  const when = (() => { try { return format(new Date(r.created_at), "d MMM yyyy", { locale: fr }); } catch { return ""; } })();
  const raterId = r.rater_id;
  return (
    <div className="p-4 rounded-xl bg-zinc-50 border border-zinc-100" data-testid={`review-${prefix}-${index}`}>
      <div className="flex items-start justify-between mb-2 gap-3">
        <Link to={raterId ? `/users/${raterId}` : "#"} className="flex items-center gap-2 min-w-0 hover:opacity-80 transition-opacity" data-testid={`review-${prefix}-${index}-rater-link`}>
          <div className="h-9 w-9 rounded-full bg-emerald-100 flex items-center justify-center overflow-hidden flex-shrink-0">
            {photoUrl ? <img src={photoUrl} alt={r.rater_name} className="w-full h-full object-cover" /> : <span className="text-sm font-bold text-emerald-700">{initial}</span>}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-zinc-900 truncate hover:text-emerald-700">{r.rater_name}</p>
            {when && <p className="text-[11px] text-zinc-400">{when}</p>}
          </div>
        </Link>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          {[1,2,3,4,5].map(s => <Star key={s} className={`h-3.5 w-3.5 ${s <= r.rating ? "fill-amber-400 text-amber-400" : "text-zinc-300"}`} />)}
        </div>
      </div>
      {r.space_title && (
        <p className="text-[11px] text-emerald-700 font-medium flex items-center gap-1 mb-1.5" data-testid={`review-${prefix}-${index}-space`}>
          <MapPin className="h-3 w-3" /> Séjour : {r.space_title}
        </p>
      )}
      {r.comment && <p className="text-sm text-zinc-600 italic">&ldquo;{r.comment}&rdquo;</p>}
    </div>
  );
}
